import { BadgeCheck, ChevronDown } from 'lucide-react'
import { stageLabel } from '@/lib/watch/rules'
import type { PublicProfile } from '@/lib/watch/types'
import { cn } from '@/lib/utils'
import { HostAvatar } from './HostAvatar'
import './watch.css'

export interface ChatHeadProps {
  host: PublicProfile
  /**
   * How many are live on the stage, the host included: seats in the room's metadata whose participant is in the room
   * (a seat can outlive its tab); none shows no count.
   */
  onStage: number
  /** A host is live on the stage while the room is connected: the avatar's live ring, and "live on stage" in words. */
  hostLive?: boolean
  /**
   * The phone on its side: the compact avatar and the name alone (no counts' line), in the short chat layer over the
   * video, so the head stays the avatar's height and the messages keep the rest.
   */
  compact?: boolean
  /**
   * The event has other stages (a phone's chat head is its stage switcher): the head is a button that opens the list of
   * stages, named for the stage and what it does ("Shane’s stage, live, 2 on stage. Change stage").
   */
  onOpenStages?: () => void
  /** The list of stages is open (the button's `aria-expanded`). */
  stagesOpen?: boolean
  className?: string
}

/**
 * The head of the stage's chat (docs/features/prototypes/watch-experience, the owner row): the host's avatar, ringed
 * while a host is live on the stage, their name with the verified mark, and a quiet line with how many are on the
 * stage (not when compact). Screen readers hear "Hosted by Transcend, Verified, live on stage", then the count. How
 * many watch is the audience row's, under the head (`Audience`), so the head doesn't say it again. A plain row, not a
 * heading: the panel's visually hidden "Chat" h2 stays the region's name. The prototype's Follow button waits for
 * follows (step 3); nothing here pretends to follow. On a phone, with other stages on the event, the head is the stage
 * switcher's button (the prototype's stage pill): a chevron after it, and a tap opens the list of stages.
 */
export function ChatHead({ host, onStage, hostLive = false, compact = false, onOpenStages, stagesOpen = false, className }: ChatHeadProps) {
  const staged = onStage > 0
  const classes = cn('watch-chat__head', compact && 'watch-chat__head--compact', onOpenStages && 'watch-chat__head--switch', className)
  // Spans laid out as the rows they are, so the same head can sit inside the button.
  const content = (
    <>
      <HostAvatar host={host} size={compact ? 'compact' : 'default'} live={hostLive} />
      <span className="watch-chat__head-who">
        <span className="watch-chat__head-name">
          <span className="sr-only">Hosted by </span>
          {/* A name can carry direction characters: isolated, so they can't reorder what follows it. */}
          <bdi className="watch-chat__head-title">{host.name}</bdi>
          {host.verified && <BadgeCheck className="watch-chat__verified" role="img" aria-label="Verified" />}
          {hostLive && <span className="sr-only">, live on stage</span>}
        </span>
        {/* Nobody on the stage (or the room not connected yet): the name centres alone in the room kept for both lines. */}
        {!compact && staged && <span className="watch-chat__head-meta">{onStage} on stage</span>}
      </span>
    </>
  )
  if (!onOpenStages) return <div className={classes}>{content}</div>
  return (
    <button
      type="button"
      className={classes}
      aria-haspopup="dialog"
      aria-expanded={stagesOpen}
      aria-label={`${stageLabel(host.name, onStage)}. Change stage`}
      onClick={onOpenStages}
    >
      {content}
      <ChevronDown className="watch-chat__head-chevron" aria-hidden="true" />
    </button>
  )
}
